import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ParticleField, DataStream, MorphBlob, FlowLines, NodeNetwork } from "@/components/effects/DynamicBackgrounds";
import { SEOHead } from "@/components/seo/SEOHead";
import { 
  Database, 
  BarChart3, 
  FileText, 
  ArrowRight, 
  Cpu, 
  Shield, 
  Zap, 
  RefreshCw,
  Brain,
  AlertTriangle,
  TrendingUp,
  ArrowDown,
  Layers,
  CheckCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const modules = [
  {
    id: "data-refinery",
    name: "DataRefinery",
    icon: Database,
    description: "Clean Data",
    color: "#00B4D8",
    outputs: ["Classified transactions", "Normalized suppliers", "Quality scores"]
  },
  {
    id: "spendlens",
    name: "SpendLens",
    icon: BarChart3,
    description: "Insights",
    color: "#10B981",
    outputs: ["Spend analytics", "Savings opportunities", "Action plans"]
  },
  {
    id: "contrack",
    name: "ConTrack",
    icon: FileText,
    description: "Governance",
    color: "#8B5CF6",
    outputs: ["Risk assessments", "Obligation tracking", "Executive summaries"]
  }
];

const aiCapabilities = [
  {
    icon: Brain,
    title: "Pattern Recognition",
    description: "Identifies spending patterns, anomalies, and trends across your entire data set"
  },
  {
    icon: AlertTriangle,
    title: "Risk Detection",
    description: "Automatically flags contract risks, compliance issues, and supplier dependencies"
  },
  {
    icon: TrendingUp,
    title: "Predictive Intelligence",
    description: "Forecasts spend trends and surfaces optimization opportunities"
  },
  {
    icon: Zap,
    title: "Action Generation",
    description: "Creates prioritized action plans with specific recommendations"
  }
];

export const PlatformPage = () => {
  return (
    <div className="bg-[#020617] pt-20">
      <SEOHead
        title="Platform"
        description="ProcureAide's AI-powered procurement platform combines DataRefinery, SpendLens and ConTrack to deliver end-to-end procurement intelligence for organisations in Pakistan and Canada."
        path="/platform"
      />
      {/* Hero */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 grid-pattern-animated opacity-30" />
        <ParticleField count={22} />
        <DataStream count={5} className="opacity-30" />
        <MorphBlob color="rgba(0,180,216,0.06)" size="500px" className="top-[5%] right-[-10%]" />
        <MorphBlob color="rgba(10,36,99,0.1)" size="400px" className="bottom-[10%] left-[-8%]" />
        <div className="absolute top-1/4 left-0 w-[500px] h-[500px] bg-[#00B4D8]/5 rounded-full blur-[100px]" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.p 
              variants={fadeInUp}
              className="text-xs uppercase tracking-[0.2em] text-[#00B4D8] mb-4"
            >
              The Procureaide Platform
            </motion.p>
            <motion.h1 
              variants={fadeInUp}
              className="text-4xl sm:text-5xl font-bold text-[#F8FAFC] leading-tight tracking-tight mb-6"
            >
              One Intelligent Ecosystem.{" "}
              <span className="text-[#00B4D8]">Infinite Possibilities.</span>
            </motion.h1>
            <motion.p 
              variants={fadeInUp}
              className="text-lg text-[#94A3B8] mb-8"
            >
              Three powerful modules connected by an AI layer that learns from your data 
              and delivers insights tailored to your operations.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex justify-center gap-4">
              <Link to="/#demo">
                <Button 
                  size="lg"
                  className="bg-[#00B4D8] text-[#020617] font-bold hover:bg-[#0096B4] hover:scale-105 transition-all shadow-[0_0_20px_rgba(0,180,216,0.3)]"
                  data-testid="platform-try-demo-btn"
                >
                  Preview Platform
                </Button>
              </Link>
              <a href="mailto:info@procureaide.com">
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-[#00B4D8] text-[#00B4D8] hover:bg-[#00B4D8]/10"
                  data-testid="platform-book-demo-btn"
                >
                  Book Demo
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Visual Ecosystem Diagram - CRITICAL */}
      <section className="py-20 bg-[#0B1426]/50 relative overflow-hidden">
        <NodeNetwork count={12} className="opacity-25" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight mb-4"
            >
              System Architecture
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-[#94A3B8] max-w-2xl mx-auto"
            >
              AI sits across everything — connecting data, analytics, and governance
            </motion.p>
          </motion.div>

          {/* Architecture Diagram */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative max-w-4xl mx-auto"
          >
            {/* AI Layer - Top */}
            <div className="bg-gradient-to-r from-[#00B4D8]/10 via-[#00B4D8]/20 to-[#00B4D8]/10 border border-[#00B4D8]/30 rounded-2xl p-6 mb-8">
              <div className="flex items-center justify-center gap-4 mb-4">
                <Brain className="w-8 h-8 text-[#00B4D8]" />
                <h3 className="text-xl font-bold text-[#F8FAFC]">AI Intelligence Layer</h3>
              </div>
              <p className="text-center text-[#94A3B8] text-sm mb-4">
                Machine Learning • Natural Language Processing • Pattern Recognition
              </p>
              <div className="grid grid-cols-4 gap-4 text-center text-xs">
                <div className="bg-[#020617]/50 rounded-lg p-3">
                  <span className="text-[#00B4D8]">Classifies</span>
                  <p className="text-[#64748B] mt-1">Your data</p>
                </div>
                <div className="bg-[#020617]/50 rounded-lg p-3">
                  <span className="text-[#00B4D8]">Analyses</span>
                  <p className="text-[#64748B] mt-1">Patterns</p>
                </div>
                <div className="bg-[#020617]/50 rounded-lg p-3">
                  <span className="text-[#00B4D8]">Detects</span>
                  <p className="text-[#64748B] mt-1">Risks</p>
                </div>
                <div className="bg-[#020617]/50 rounded-lg p-3">
                  <span className="text-[#00B4D8]">Generates</span>
                  <p className="text-[#64748B] mt-1">Actions</p>
                </div>
              </div>
            </div>

            {/* Connection Lines */}
            <div className="flex justify-center mb-4">
              <div className="flex gap-20">
                <ArrowDown className="w-6 h-6 text-[#00B4D8]/50" />
                <ArrowDown className="w-6 h-6 text-[#10B981]/50" />
                <ArrowDown className="w-6 h-6 text-[#8B5CF6]/50" />
              </div>
            </div>

            {/* Modules Grid */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {modules.map((module, index) => (
                <motion.div
                  key={module.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Link 
                    to={`/products/${module.id}`}
                    className="block bg-[#111F36] border rounded-xl p-6 h-full hover:border-opacity-50 transition-all group"
                    style={{ borderColor: `${module.color}30` }}
                    data-testid={`platform-module-${module.id}`}
                  >
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                      style={{ backgroundColor: `${module.color}20` }}
                    >
                      <module.icon className="w-6 h-6" style={{ color: module.color }} />
                    </div>
                    <h3 className="text-lg font-bold text-[#F8FAFC] mb-1">{module.name}</h3>
                    <p className="text-sm mb-4" style={{ color: module.color }}>{module.description}</p>
                    <ul className="space-y-2">
                      {module.outputs.map((output, i) => (
                        <li key={i} className="flex items-center gap-2 text-xs text-[#94A3B8]">
                          <CheckCircle className="w-3 h-3" style={{ color: module.color }} />
                          {output}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-4 flex items-center text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: module.color }}>
                      Learn more <ArrowRight className="w-4 h-4 ml-1" />
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>

            {/* Output Row */}
            <div className="flex justify-center mb-4">
              <div className="flex gap-20">
                <ArrowDown className="w-6 h-6 text-[#00B4D8]/50" />
                <ArrowDown className="w-6 h-6 text-[#10B981]/50" />
                <ArrowDown className="w-6 h-6 text-[#8B5CF6]/50" />
              </div>
            </div>

            {/* Unified Output */}
            <div className="bg-[#111F36] border border-[#10B981]/30 rounded-2xl p-6 text-center">
              <Layers className="w-8 h-8 text-[#10B981] mx-auto mb-3" />
              <h3 className="text-lg font-bold text-[#F8FAFC] mb-2">Unified Intelligence</h3>
              <p className="text-[#94A3B8] text-sm">
                Actionable insights • Executive dashboards • Automated reports • Smart alerts
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* AI Capabilities */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight mb-4"
            >
              What the AI Layer Does
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-[#94A3B8] max-w-2xl mx-auto"
            >
              Intelligence embedded into every workflow — not layered on top
            </motion.p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {aiCapabilities.map((cap, index) => (
              <motion.div
                key={cap.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-[#0B1426] border border-[#00B4D8]/20 rounded-xl p-6 hover:border-[#00B4D8]/40 transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-[#00B4D8]/10 flex items-center justify-center mb-4">
                  <cap.icon className="w-6 h-6 text-[#00B4D8]" />
                </div>
                <h3 className="text-lg font-semibold text-[#F8FAFC] mb-2">{cap.title}</h3>
                <p className="text-sm text-[#94A3B8]">{cap.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#0B1426]/50 relative overflow-hidden">
        <FlowLines count={3} className="opacity-20" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl font-bold text-[#F8FAFC] tracking-tight mb-4"
            >
              See It in Action
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-[#94A3B8] mb-8"
            >
              Try the platform walkthrough or schedule a personalized demo.
            </motion.p>
            <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-4">
              <Link to="/#demo">
                <Button 
                  size="lg"
                  className="bg-[#00B4D8] text-[#020617] font-bold hover:bg-[#0096B4] hover:scale-105 transition-all shadow-[0_0_20px_rgba(0,180,216,0.3)] px-8"
                  data-testid="platform-cta-demo-btn"
                >
                  Preview Platform
                </Button>
              </Link>
              <a href="mailto:info@procureaide.com">
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-[#00B4D8] text-[#00B4D8] hover:bg-[#00B4D8]/10 px-8"
                  data-testid="platform-cta-book-btn"
                >
                  Book Demo
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PlatformPage;
